/* eslint-disable react-hooks/set-state-in-effect, react/no-unescaped-entities */
/**
 * MyCoachingTab — Sprint 3 V3 (Dark Luxury)
 * =========================================
 * Onglet "Coaching IA" dans le Cockpit agent.
 * Affiche les 6 axes radar + diagnostic Claude + 3 actions hebdo + formation suggérée.
 * Migré en thème Dark Luxury (lisibilité sur fond sombre du cockpit).
 */
import { useEffect, useState } from 'react';
import api from '../../lib/api';
import {
    Sparkles, Target, Trophy, TrendingDown, ChevronRight,
    BookOpen, RefreshCw,
} from 'lucide-react';
import AIFeedbackWidget from '../ai/AIFeedbackWidget';
import AIThinkingAnimation from '../ai/AIThinkingAnimation';
import CopilotRecognition from '../engagement/CopilotRecognition';

const AXIS_ORDER = ['prospection', 'signature', 'commercialisation', 'suivi', 'closing', 'satisfaction'];

export default function MyCoachingTab({ user }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState(null);

    const fetchCoaching = async (regen = false) => {
        try {
            if (regen) setRefreshing(true);
            else setLoading(true);
            const res = await api.get(
                `/ai/coaching/agent/${user.id}${regen ? '?regenerate=true' : ''}`,
                { timeout: 60000 },
            );
            setData(res.data);
            setError(null);
        } catch (e) {
            setError(e?.response?.data?.detail || e.message);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    useEffect(() => {
        if (user?.id) fetchCoaching(false);
    }, [user?.id]);

    if (loading) {
        return (
            <div data-testid="my-coaching-tab-loading" className="space-y-5">
                <CopilotRecognition />
                <AIThinkingAnimation />
            </div>
        );
    }

    if (error || !data) {
        return (
            <div className="space-y-5">
                <CopilotRecognition />
                <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-5 text-sm text-white/60" data-testid="my-coaching-tab-error">
                    Coaching personnalisé temporairement indisponible.
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-5" data-testid="my-coaching-tab">
            {/* M10 — Le Coach reconnaît (reconnaissance factuelle variable) */}
            <CopilotRecognition />

            {/* Hero — Diagnostic */}
            <div className="bg-gradient-to-br from-[#681793]/20 via-[#FF914D]/10 to-transparent border border-white/10 rounded-2xl p-6">
                <div className="flex items-start gap-4">
                    <div className="bg-gradient-to-br from-[#681793] to-[#FF914D] p-3 rounded-xl text-white shrink-0">
                        <Sparkles className="w-6 h-6" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                            <span className="text-[10px] font-bold uppercase tracking-wider text-[#C8A2FF] bg-[#681793]/25 border border-[#A855F7]/30 px-2 py-1 rounded">
                                Coaching IA · Coach
                            </span>
                            <button
                                onClick={() => fetchCoaching(true)}
                                disabled={refreshing}
                                className="text-white/40 hover:text-white p-1 transition-colors"
                                title="Régénérer"
                                data-testid="my-coaching-refresh"
                            >
                                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                            </button>
                        </div>
                        <h2 className="text-xl md:text-2xl font-bold text-white leading-tight mb-3">
                            {data.headline}
                        </h2>
                        <p className="text-white/70 text-sm md:text-base leading-relaxed">{data.diagnostic}</p>
                    </div>
                </div>
            </div>

            {/* Radar 6 axes */}
            <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-5">
                <h3 className="text-xs font-bold uppercase tracking-wider text-white/50 mb-4">Profil 6 axes (vs équipe / benchmark Top 20%)</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {AXIS_ORDER.map((axis) => {
                        const me = data.agent_axes[axis] || 0;
                        const team = data.team_avg_axes[axis] || 0;
                        const top = data.top_performer_axes[axis] || 0;
                        const labels = {
                            prospection: 'Prospection',
                            signature: 'Signature',
                            commercialisation: 'Commercialisation',
                            suivi: 'Suivi',
                            closing: 'Closing',
                            satisfaction: 'Satisfaction',
                        };
                        const colorClass = me >= 70 ? 'bg-emerald-500' : me >= 50 ? 'bg-amber-500' : 'bg-red-500';
                        return (
                            <div key={axis} className="space-y-1.5">
                                <div className="flex items-center justify-between text-xs">
                                    <span className="font-medium text-white/80">{labels[axis]}</span>
                                    <span className="font-bold tabular-nums text-white">{me}</span>
                                </div>
                                <div className="h-2 bg-white/10 rounded-full overflow-hidden relative">
                                    <div className={`h-full ${colorClass}`} style={{ width: `${me}%` }}></div>
                                    {/* benchmark marker */}
                                    <div className="absolute top-0 h-full border-l-2 border-white/70" style={{ left: `${top}%` }} title="Top performer équipe"></div>
                                </div>
                                <div className="text-[10px] text-white/40 flex justify-between">
                                    <span>équipe : {team}</span>
                                    <span>top : {top}</span>
                                </div>
                            </div>
                        );
                    })}
                </div>
                <p className="text-[10px] text-white/30 mt-4">La barre blanche représente le meilleur agent de l'équipe sur cet axe.</p>
            </div>

            {/* Forces & faiblesses */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-2xl p-5">
                    <div className="flex items-center gap-2 mb-3">
                        <Trophy className="w-4 h-4 text-emerald-400" />
                        <h3 className="text-xs font-bold uppercase tracking-wider text-emerald-300">Tes forces</h3>
                    </div>
                    <div className="space-y-3">
                        {(data.strengths || []).slice(0, 2).map((s, i) => (
                            <div key={i}>
                                <p className="text-sm font-bold text-white">{s.axis_label}</p>
                                <p className="text-xs text-white/70 mt-1">{s.praise}</p>
                                {s.evidence && <p className="text-[10px] text-white/40 mt-1 italic">{s.evidence}</p>}
                            </div>
                        ))}
                        {(!data.strengths || data.strengths.length === 0) && (
                            <p className="text-xs text-white/50 italic">Continue à progresser — tes premières forces vont émerger.</p>
                        )}
                    </div>
                </div>
                <div className="bg-amber-500/10 border border-amber-500/20 rounded-2xl p-5">
                    <div className="flex items-center gap-2 mb-3">
                        <TrendingDown className="w-4 h-4 text-amber-400" />
                        <h3 className="text-xs font-bold uppercase tracking-wider text-amber-300">À travailler</h3>
                    </div>
                    <div className="space-y-3">
                        {(data.weaknesses || []).slice(0, 2).map((w, i) => (
                            <div key={i}>
                                <p className="text-sm font-bold text-white">{w.axis_label}</p>
                                <p className="text-xs text-white/70 mt-1">{w.issue}</p>
                                {w.impact && <p className="text-[10px] text-white/40 mt-1 italic">Impact : {w.impact}</p>}
                            </div>
                        ))}
                        {(!data.weaknesses || data.weaknesses.length === 0) && (
                            <p className="text-xs text-white/50 italic">Tu es au-dessus des benchmarks sur tous les axes — excellent !</p>
                        )}
                    </div>
                </div>
            </div>

            {/* Actions semaine */}
            <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-5">
                <div className="flex items-center gap-2 mb-4">
                    <Target className="w-4 h-4 text-[#A855F7]" />
                    <h3 className="text-xs font-bold uppercase tracking-wider text-white/70">3 actions pour cette semaine</h3>
                </div>
                <div className="space-y-3">
                    {(data.actions_this_week || []).map((a, i) => (
                        <div key={i} className="bg-white/[0.03] border border-white/10 rounded-xl p-4">
                            <div className="flex items-start gap-3">
                                <div className="bg-gradient-to-br from-[#681793] to-[#FF914D] text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center shrink-0">
                                    {i + 1}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <h4 className="text-sm font-bold text-white">{a.title}</h4>
                                        {a.deadline && (
                                            <span className="text-[10px] font-bold uppercase tracking-wider bg-[#681793]/25 text-[#C8A2FF] border border-[#A855F7]/30 px-2 py-0.5 rounded">
                                                {a.deadline}
                                            </span>
                                        )}
                                    </div>
                                    <p className="text-xs text-white/70 mt-1">{a.what_to_do}</p>
                                    {a.expected_impact && (
                                        <p className="text-[11px] text-emerald-300 mt-1.5 flex items-center gap-1">
                                            <ChevronRight className="w-3 h-3" /> {a.expected_impact}
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Formation suggérée */}
            {data.suggested_training?.title && (
                <div className="bg-gradient-to-br from-[#0F0F12] to-[#1A1A1F] border border-white/10 text-white rounded-2xl p-5">
                    <div className="flex items-start gap-3">
                        <div className="bg-emerald-500/20 p-2 rounded-lg shrink-0">
                            <BookOpen className="w-5 h-5 text-emerald-400" />
                        </div>
                        <div className="flex-1">
                            <p className="text-[10px] font-bold uppercase tracking-wider text-emerald-400">Ressource suggérée · {data.suggested_training.type}</p>
                            <p className="text-base font-bold mt-1">{data.suggested_training.title}</p>
                            <p className="text-xs text-white/70 mt-2">{data.suggested_training.description}</p>
                        </div>
                    </div>
                </div>
            )}

            {/* Feedback IA */}
            <div className="flex justify-end pt-2">
                <AIFeedbackWidget feature="agent_coaching" modelUsed={data.model_used} />
            </div>
        </div>
    );
}
